import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, interval, of } from 'rxjs';
import { switchMap, catchError, map, startWith } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { GraphQLService } from './graphql.service';
import { BaseApiService } from '../api/base-api.service';
import { API_CONFIG } from '../api/api.config';

export interface GatewayHealth {
  status: 'ok' | 'error';
  timestamp?: Date;
  uptime?: number;
}

export interface ServiceStatus {
  name: string;
  status: 'up' | 'down' | 'degraded';
  responseTime?: number;
  lastCheck?: Date;
  version?: string;
}

export interface GatewayReadiness {
  status: 'ready' | 'not_ready';
  checks: {
    auth?: 'up' | 'down';
    user?: 'up' | 'down';
    task?: 'up' | 'down';
    project?: 'up' | 'down';
    platform?: 'up' | 'down';
    reporting?: 'up' | 'down';
    audit?: 'up' | 'down';
    notifications?: 'up' | 'down';
  };
  services?: ServiceStatus[];
}

@Injectable({ providedIn: 'root' })
export class GatewayService extends BaseApiService {
  private readonly graphqlService = inject(GraphQLService);
  private readonly _health = signal<GatewayHealth | null>(null);
  private readonly _readiness = signal<GatewayReadiness | null>(null);
  private readonly _services = signal<ServiceStatus[]>([]);

  readonly health = computed(() => this._health());
  readonly readiness = computed(() => this._readiness());
  readonly services = computed(() => this._services());
  readonly isHealthy = computed(() => this._health()?.status === 'ok');
  readonly isReady = computed(() => this._readiness()?.status === 'ready');

  protected get baseUrl(): string {
    return API_CONFIG.getUrl(API_CONFIG.endpoints.gateway);
  }

  constructor() {
    super();
    this.startHealthMonitoring();
  }

  /**
   * Check gateway health
   */
  getHealth(): Observable<GatewayHealth> {
    return this.get<GatewayHealth>('/health').pipe(
      map(health => {
        this._health.set(health);
        return health;
      }),
      catchError(error => {
        this._health.set({ status: 'error' });
        throw error;
      })
    );
  }

  /**
   * Check gateway readiness (all services)
   */
  getReadiness(): Observable<GatewayReadiness> {
    return this.get<GatewayReadiness>('/health/ready').pipe(
      map(readiness => {
        this._readiness.set(readiness);
        if (readiness.services) {
          this._services.set(readiness.services);
        }
        return readiness;
      }),
      catchError(error => {
        this._readiness.set({ status: 'not_ready', checks: {} });
        throw error;
      })
    );
  }

  /**
   * Check GraphQL Gateway health
   */
  checkGraphQLHealth(): Observable<boolean> {
    const query = `
      query {
        __typename
      }
    `;

    return this.graphqlService.query(query).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  /**
   * Get service status
   */
  getServiceStatus(serviceName: string): ServiceStatus | null {
    return this._services().find(s => s.name === serviceName) || null;
  }

  /**
   * Check if service is up
   */
  isServiceUp(serviceName: string): boolean {
    const status = this.getServiceStatus(serviceName);
    return status?.status === 'up';
  }

  /**
   * Start automatic health monitoring
   */
  private startHealthMonitoring(): void {
    // Check health every 30 seconds
    interval(30000).pipe(
      startWith(0),
      switchMap(() => this.getHealth().pipe(
        catchError(() => of({ status: 'error' as const }))
      ))
    ).subscribe();
  }

  /**
   * Get all service statuses
   */
  getAllServices(): ServiceStatus[] {
    return this._services();
  }

  /**
   * Get services by status
   */
  getServicesByStatus(status: 'up' | 'down' | 'degraded'): ServiceStatus[] {
    return this._services().filter(s => s.status === status);
  }

  /**
   * Refresh all statuses
   */
  refreshStatuses(): Observable<void> {
    return this.getReadiness().pipe(
      map(() => undefined),
      catchError(() => of(undefined))
    );
  }
}

